import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type ServiceRow = Database['public']['Tables']['services']['Row'];

export interface ServiceInput {
  name: string;
  description?: string | null;
  duration_minutes: number;
  price_cents: number;
  currency?: string;
  vat_rate_percent?: number;
  color?: string;
  is_active?: boolean;
  /** If true, this is a calendar blocker (lunch, meeting, etc.) that doesn't require a patient. CLISUP-38. */
  is_blocker?: boolean;
  /** Forms template IDs to auto-send when an appointment of this type is completed. FORMS-8. */
  auto_send_questionnaires?: string[];
  /** Single Forms template ID to send immediately when patient books online. FORMS-10. */
  pre_visit_questionnaire_template_id?: string | null;
}

export const servicesListQueryKey = (clinicId: string | null, includeInactive: boolean) =>
  ['services', 'list', clinicId, { includeInactive }] as const;

export function useServicesList(
  clinicId: string | null | undefined,
  options: { includeInactive?: boolean } = {},
) {
  const includeInactive = Boolean(options.includeInactive);
  return useQuery({
    queryKey: servicesListQueryKey(clinicId ?? null, includeInactive),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ServiceRow[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('services')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('name', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function useCreateService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceInput): Promise<ServiceRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase
        .from('services')
        .insert({
          clinic_id: clinicId,
          name: input.name,
          description: input.description ?? null,
          duration_minutes: input.duration_minutes,
          price_cents: input.price_cents,
          currency: input.currency ?? 'DKK',
          vat_rate_percent: input.vat_rate_percent ?? 25,
          color: input.color ?? '#6366F1',
          is_active: input.is_active ?? true,
          is_blocker: input.is_blocker ?? false,
          auto_send_questionnaires: input.auto_send_questionnaires ?? null,
          pre_visit_questionnaire_template_id: input.pre_visit_questionnaire_template_id ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as ServiceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
    },
  });
}

export function useUpdateService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<ServiceInput>): Promise<ServiceRow> => {
      const { id, auto_send_questionnaires, pre_visit_questionnaire_template_id, ...rest } = input;
      
      type ServiceUpdate = Database['public']['Tables']['services']['Update'];
      const patch: Partial<ServiceUpdate> = { ...rest };
      
      if (auto_send_questionnaires !== undefined) {
        patch.auto_send_questionnaires = auto_send_questionnaires ?? null;
      }
      if (pre_visit_questionnaire_template_id !== undefined) {
        patch.pre_visit_questionnaire_template_id = pre_visit_questionnaire_template_id ?? null;
      }
      
      const { data, error } = await supabase
        .from('services')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as ServiceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
    },
  });
}

export function useDeleteService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
    },
  });
}
